import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import CircularProgress from '@mui/material/CircularProgress';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import Pick from './Pick';
import SortButton from './SortButton';
import { getAuth } from 'firebase/auth';
import { STRATEGY_MAPPINGS } from '../constants/strategies';

const ODIKAI_API_BASE = process.env.REACT_APP_ODIKAI_API_BASE || '/api/odikai';

export default function PlaybookView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [playbook, setPlaybook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortField, setSortField] = useState('weight');
  const [sortDirection, setSortDirection] = useState('desc');
  const [isRenaming, setIsRenaming] = useState(false);
  const [newName, setNewName] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    strategyWeights: false,
    recommendedPicks: false
  });

  useEffect(() => {
    async function fetchPlaybook() {
      if (!user || !id) return;

      try {
        const playbookRef = doc(db, 'user_playbooks', id);
        const playbookDoc = await getDoc(playbookRef);

        if (!playbookDoc.exists()) {
          setError('Playbook not found');
          return;
        }

        const data = playbookDoc.data();
        if (data.user_id !== user.uid) {
          setError('You do not have permission to view this playbook');
          return;
        }

        setPlaybook({ id: playbookDoc.id, ...data });
      } catch (err) {
        setError('Failed to load playbook');
      } finally {
        setLoading(false);
      }
    }

    fetchPlaybook();
  }, [id, user]);

  const formatDate = (date) => {
    if (!date) return 'N/A';
    // Create date object and adjust for timezone
    const dateObj = new Date(date);
    const timezoneOffset = dateObj.getTimezoneOffset() * 60000; // Convert minutes to milliseconds
    const adjustedDate = new Date(dateObj.getTime() + timezoneOffset);
    
    return adjustedDate.toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: '2-digit',
      timeZone: 'UTC' // Force UTC to prevent double timezone conversion
    });
  };

  const formatPercent = (value) => {
    if (!value && value !== 0) return 'N/A';
    return `${(value * 100).toFixed(2)}%`;
  };

  const formatDollar = (value) => {
    if (!value && value !== 0) return 'N/A';
    return `$${value.toFixed(2)}`;
  };

  const handleSort = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const handleRename = async () => {
    if (!newName.trim()) return;
    
    try {
      const playbookRef = doc(db, 'user_playbooks', id);
      await updateDoc(playbookRef, {
        name: newName.trim(),
        last_updated: new Date().toISOString()
      });
      
      setPlaybook({
        ...playbook,
        name: newName.trim(),
        last_updated: new Date().toISOString()
      });
      setIsRenaming(false);
    } catch (err) {
      setError('Failed to rename playbook');
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) {
      setDeleteConfirm(true);
      return;
    }

    try {
      await deleteDoc(doc(db, 'user_playbooks', id));
      navigate('/playbooks');
    } catch (err) {
      setError('Failed to delete playbook');
    }
  };

  const handleRefresh = async () => {
    try {
      setIsRefreshing(true);
      const auth = getAuth();
      const currentUser = auth.currentUser;
      if (!currentUser) {
        navigate('/login');
        return;
      }
      const idToken = await currentUser.getIdToken(true); // force refresh
      const response = await fetch(`${ODIKAI_API_BASE}/picks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          portfolio: playbook.portfolio
        })
      });
      if (!response.ok) {
        throw new Error('Failed to refresh playbook');
      }
      const data = await response.json();
      // Update the playbook with new data
      const playbookRef = doc(db, 'user_playbooks', id);
      await updateDoc(playbookRef, {
        last_updated: new Date().toISOString(),
        event_name: data.event_name,
        event_date: data.event_date,
        picks: data.picks.map(pick => ({
          pick_name: pick.pick,
          opponent_name: pick.opponent,
          budget_amount: pick.augmented_weight,
          american_odds: pick.odds_us,
          decimal_odds: parseFloat(pick.odds),
          supporting_strategies: pick.strategy?.length || 0,
          type: pick.pick_type.toLowerCase()
        })),
      });
      setPlaybook(prev => ({
        ...prev,
        last_updated: new Date().toISOString(),
        event_name: data.event_name,
        event_date: data.event_date,
        picks: data.picks.map(pick => ({
          pick_name: pick.pick,
          opponent_name: pick.opponent,
          budget_amount: pick.augmented_weight,
          american_odds: pick.odds_us,
          decimal_odds: parseFloat(pick.odds),
          supporting_strategies: pick.strategy?.length || 0,
          type: pick.pick_type.toLowerCase()
        })),
      }));
    } catch (error) {
      setError('Failed to refresh playbook');
    } finally {
      setIsRefreshing(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Helper: invert STRATEGY_MAPPINGS for display
  function getFriendlyStrategyName(columnName) {
    for (const [strategyName, options] of Object.entries(STRATEGY_MAPPINGS)) {
      for (const [option, col] of Object.entries(options)) {
        if (col === columnName) {
          return `${strategyName} (${option})`;
        }
      }
    }
    return columnName;
  }

  if (loading) {
    return (
      <div className="simple-builder-section">
        <div className="loading-screen">
          <div className="loading-spinner"></div>
          <h2>Loading Playbook...</h2>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="simple-builder-section">
        <div className="error-message">{error}</div>
        <button 
          className="nav-button back-button" 
          onClick={() => navigate('/playbooks')}
          style={{ marginTop: '1rem' }}
        >
          Back to Playbooks
        </button>
      </div>
    );
  }

  if (!playbook) return null;

  return (
    <div className="simple-builder-section">
      <div className="results-view">
        <div className="picks-display">
          <div className="section-header">
            <div className="header-content">
              <div className="title-section">
                {isRenaming ? (
                  <div className="rename-input-group">
                    <input
                      type="text"
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      placeholder="Enter new name"
                      className="rename-input"
                      autoFocus
                    />
                    <div className="rename-actions">
                      <button 
                        className="rename-button save"
                        onClick={handleRename}
                        disabled={!newName.trim()}
                      >
                        Save
                      </button>
                      <button 
                        className="rename-button cancel"
                        onClick={() => {
                          setIsRenaming(false);
                          setNewName('');
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <h2>
                    <ShowChartIcon sx={{ fontSize: 24, marginRight: '0.5rem' }} />
                    {playbook.name}
                  </h2>
                )}
              </div>
              <div className="header-actions">
                {playbook.event_date && new Date(playbook.event_date) < new Date() && (
                  <button 
                    className="action-button refresh"
                    onClick={handleRefresh}
                    disabled={isRefreshing}
                  >
                    {isRefreshing ? (
                      <>
                        <CircularProgress size={20} color="inherit" /> Refreshing...
                      </>
                    ) : (
                      <>
                        <RefreshIcon /> Refresh Picks
                      </>
                    )}
                  </button>
                )}
                <button 
                  className="action-button rename"
                  onClick={() => {
                    setNewName(playbook.name);
                    setIsRenaming(true);
                  }}
                  disabled={isRefreshing}
                >
                  <EditIcon /> Rename
                </button>
                <button 
                  className={`action-button delete ${deleteConfirm ? 'confirm' : ''}`}
                  onClick={handleDelete}
                  disabled={isRefreshing}
                >
                  <DeleteIcon />
                  {deleteConfirm ? 'Confirm Delete' : 'Delete'}
                </button>
              </div>
            </div>
            
            <div className="results-summary">
              <div className="summary-item">
                <h3>Event</h3>
                <p>{playbook.event_name || 'No event selected'}</p>
                <p className="date">{formatDate(playbook.event_date)}</p>
              </div>
              <div className="summary-item">
                <h3>Average Returns</h3>
                <p>{formatPercent(playbook.portfolio_returns)}</p>
              </div>
              <div className="summary-item">
                <h3>Previous Year Returns</h3>
                <p>{formatDollar(playbook.previous_year_returns)}</p>
              </div>
            </div>
          </div>

          <div className="accordion-sections">
            <div className="accordion-item">
              <button 
                className="accordion-header"
                onClick={() => toggleSection('strategyWeights')}
              >
                <h3>{playbook.playbook_type === 'language' ? 'Playbook Description' : 'Strategy Weights'}</h3>
                {expandedSections.strategyWeights ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </button>
              {expandedSections.strategyWeights && (
                <div className="accordion-content strategy-weights">
                  {playbook.playbook_type === 'language' ? (
                    <div className="description-section" style={{ marginBottom: '2rem', background: 'var(--surface-light)', borderRadius: '12px', padding: '1.5rem' }}>
                      <p style={{ color: 'var(--text)' }}>{playbook.description}</p>
                    </div>
                  ) : (
                    playbook.portfolio?.map((item, index) => (
                      <div key={index} className="strategy-weight-item">
                        <span className="strategy-name" title={item.strategy}>{getFriendlyStrategyName(item.strategy)}</span>
                        <span className="strategy-weight">{formatPercent(item.weight)}</span>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            <div className="accordion-item">
              <button 
                className="accordion-header"
                onClick={() => toggleSection('recommendedPicks')}
              >
                <h3>Recommended Picks</h3>
                {expandedSections.recommendedPicks ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </button>
              {expandedSections.recommendedPicks && (
                <div className="accordion-content">
                  <div className="sort-controls">
                    <SortButton 
                      field="weight" 
                      label="Sort by Weight" 
                      currentField={sortField}
                      direction={sortDirection}
                      onClick={handleSort}
                    />
                    <SortButton 
                      field="odds" 
                      label="Sort by Odds" 
                      currentField={sortField}
                      direction={sortDirection}
                      onClick={handleSort}
                    />
                    <SortButton 
                      field="strategies" 
                      label="Sort by Support" 
                      currentField={sortField}
                      direction={sortDirection}
                      onClick={handleSort}
                    />
                  </div>

                  <div className="picks-list">
                    {playbook.picks
                      .sort((a, b) => {
                        let aValue, bValue;
                        switch (sortField) {
                          case 'weight':
                            aValue = a.budget_amount;
                            bValue = b.budget_amount;
                            break;
                          case 'odds':
                            aValue = a.decimal_odds;
                            bValue = b.decimal_odds;
                            break;
                          case 'strategies':
                            aValue = a.supporting_strategies;
                            bValue = b.supporting_strategies;
                            break;
                          default:
                            return 0;
                        }
                        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
                      })
                      .map((pick, index) => (
                        <Pick
                          key={index}
                          pick_name={pick.pick_name}
                          opponent_name={pick.opponent_name}
                          type={pick.type}
                          weight={pick.budget_amount}
                          american_odds={pick.american_odds}
                          decimal_odds={pick.decimal_odds}
                          supporting_strategies={pick.supporting_strategies}
                          total_strategies={10}
                        />
                      ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="action-buttons">
          <button className="nav-button back-button" onClick={() => navigate('/playbooks')}>
            Back to Playbooks
          </button>
        </div>
      </div>

      <style>
        {`
          .simple-builder-section {
            min-height: calc(100vh - 64px);
            padding: var(--spacing-lg);
            width: 100%;
            max-width: var(--max-width);
            margin: 0 auto;
            display: flex;
            flex-direction: column;
            gap: 2rem;
            box-sizing: border-box;
          }

          .results-view {
            width: 100%;
            display: flex;
            flex-direction: column;
            gap: var(--spacing-lg);
          }

          .picks-display {
            background: var(--surface);
            border-radius: var(--radius-lg);
            padding: clamp(1.5rem, 4vw, 2.5rem);
            width: 100%;
            box-sizing: border-box;
          }

          .section-header {
            margin-bottom: var(--spacing-xl);
          }

          .section-header h2 {
            font-size: clamp(1.5rem, 3vw, 1.75rem);
            font-weight: 600;
            color: var(--text);
            margin-bottom: var(--spacing-lg);
            display: flex;
            align-items: center;
          }

          .results-summary {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: var(--spacing-md);
            margin-top: var(--spacing-lg);
          }

          .summary-item {
            background: var(--surface-light);
            padding: var(--spacing-lg);
            border-radius: var(--radius-md);
            text-align: center;
          }

          .summary-item h3 {
            color: var(--text-secondary);
            font-size: clamp(0.9rem, 1.2vw, 1rem);
            margin-bottom: var(--spacing-sm);
            font-weight: 500;
          }

          .summary-item p {
            color: var(--text);
            font-size: clamp(1.1rem, 1.5vw, 1.5rem);
            font-weight: 600;
            margin: 0;
          }

          .summary-item .date {
            color: var(--text-secondary);
            font-size: clamp(0.8rem, 1vw, 0.9rem);
            font-weight: normal;
            margin-top: var(--spacing-xs);
          }

          .accordion-sections {
            display: flex;
            flex-direction: column;
            gap: var(--spacing-lg);
            margin-top: var(--spacing-xl);
          }

          .accordion-item {
            border-radius: var(--radius-md);
            overflow: hidden;
            background: var(--surface-light);
            border: 1px solid rgba(255, 255, 255, 0.05);
            transition: all 0.2s ease;
          }

          .accordion-item:hover {
            border-color: rgba(255, 255, 255, 0.1);
          }

          .accordion-header {
            width: 100%;
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: var(--spacing-lg);
            background: transparent;
            border: none;
            cursor: pointer;
            transition: all 0.2s ease;
            position: relative;
            overflow: hidden;
          }

          .accordion-header::before {
            content: '';
            position: absolute;
            inset: 0;
            background: rgba(255, 255, 255, 0.03);
            opacity: 0;
            transition: opacity 0.2s ease;
          }

          .accordion-header:hover::before {
            opacity: 1;
          }

          .accordion-header h3 {
            color: var(--text);
            font-size: clamp(1.1rem, 1.5vw, 1.25rem);
            font-weight: 600;
            margin: 0;
            display: flex;
            align-items: center;
            gap: 8px;
          }

          .accordion-header svg {
            transition: transform 0.2s ease;
          }

          .accordion-content {
            background: #1E1E1E;
            padding: var(--spacing-lg);
            border-radius: var(--radius-md);
          }

          .sort-controls {
            display: flex;
            gap: 12px;
            margin: 0 0 16px;
            flex-wrap: wrap;
            align-items: center;
          }

          .sort-button {
            background: #2C2C2C;
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 4px;
            color: #fff;
            padding: 8px 12px;
            font-size: 14px;
            cursor: pointer;
            transition: all 0.2s ease;
          }

          .sort-button:hover {
            background: #363636;
            border-color: rgba(255, 255, 255, 0.2);
          }

          .sort-button.active {
            background: #7b49d1;
            border-color: #7b49d1;
            box-shadow: 0 2px 8px rgba(123, 73, 209, 0.2);
          }

          .picks-list {
            display: flex;
            flex-direction: column;
            gap: 8px;
          }

          .action-buttons {
            display: flex;
            justify-content: flex-end;
            gap: var(--spacing-md);
            margin-top: var(--spacing-lg);
          }

          .header-content {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            gap: var(--spacing-md);
            margin-bottom: var(--spacing-lg);
          }

          .title-section {
            flex: 1;
          }

          .header-actions {
            display: flex;
            gap: var(--spacing-sm);
          }

          .action-button {
            display: flex;
            align-items: center;
            gap: 6px;
            padding: 8px 12px;
            border-radius: var(--radius-sm);
            font-size: 0.9rem;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s var(--bounce);
            border: 1px solid rgba(255, 255, 255, 0.1);
            background: var(--surface-light);
            color: var(--text);
          }

          .action-button:hover {
            background: var(--surface-hover);
          }

          .action-button.delete {
            color: #ff4d4d;
            border-color: currentColor;
          }

          .action-button.delete:hover {
            background: rgba(255, 77, 77, 0.1);
          }

          .action-button.delete.confirm {
            background: #ff4d4d;
            color: white;
            border-color: #ff4d4d;
          }

          .action-button.delete.confirm:hover {
            background: #ff3333;
          }

          .rename-input-group {
            display: flex;
            flex-direction: column;
            gap: var(--spacing-sm);
          }

          .rename-input {
            background: var(--surface-light);
            border: 1px solid var(--primary);
            border-radius: var(--radius-sm);
            padding: 8px 12px;
            color: var(--text);
            font-size: 1.1rem;
            width: 100%;
            max-width: 400px;
          }

          .rename-input:focus {
            outline: none;
            box-shadow: 0 0 0 2px rgba(123, 73, 209, 0.1);
          }

          .rename-actions {
            display: flex;
            gap: var(--spacing-sm);
          }

          .rename-button {
            padding: 6px 12px;
            border-radius: var(--radius-sm);
            font-size: 0.9rem;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s var(--bounce);
            border: none;
          }

          .rename-button.save {
            background: var(--primary);
            color: white;
          }

          .rename-button.save:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }

          .rename-button.save:not(:disabled):hover {
            background: var(--primary-hover);
          }

          .rename-button.cancel {
            background: var(--surface-light);
            color: var(--text);
            border: 1px solid rgba(255, 255, 255, 0.1);
          }

          .rename-button.cancel:hover {
            background: var(--surface-hover);
          }

          .action-button.refresh {
            background: #2196f3;
            color: white;
            border: none;
            opacity: 1;
            transition: all 0.2s ease;
          }

          .action-button.refresh:disabled {
            opacity: 0.7;
            cursor: wait;
          }

          .action-button.refresh:not(:disabled):hover {
            background: #1976d2;
            transform: translateY(-2px);
          }

          .action-button:disabled {
            opacity: 0.5;
            cursor: wait;
          }

          @media (max-width: 768px) {
            .simple-builder-section {
              padding: var(--spacing-sm);
            }

            .picks-display {
              padding: var(--spacing-lg);
            }

            .results-summary {
              grid-template-columns: 1fr;
            }

            .sort-controls {
              justify-content: flex-start;
              margin-bottom: var(--spacing-md);
            }

            .sort-button {
              flex: 1;
              text-align: center;
              padding: 8px 12px;
            }

            .action-buttons {
              flex-direction: column;
            }

            .action-buttons button {
              width: 100%;
            }

            .header-content {
              flex-direction: column;
              gap: var(--spacing-lg);
            }

            .header-actions {
              width: 100%;
            }

            .action-button {
              flex: 1;
              justify-content: center;
            }

            .rename-input {
              max-width: none;
            }

            .accordion-item {
              border-radius: 8px;
            }

            .accordion-header {
              padding: var(--spacing-md);
            }

            .accordion-content {
              padding: 0 var(--spacing-md) var(--spacing-md);
            }
          }
        `}
      </style>
    </div>
  );
} 